import React, { useState, useCallback } from 'react';
import Box from '@mui/system/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { CodeInput, CountdownTimer } from '@components';

interface CodeVerificationStepProps {
  code: string;
  onCodeChange: (value: string) => void;
  maskedPhoneNumber: string;
  /** When in token path (e.g. code sent to email), describe destination for copy */
  maskedDestination?: string;
  expiresAt: number | null;
  /** When backend returns the code in the response (e.g. dev/testing), show it and allow copy */
  returnedCode?: string | null;
  onResend: () => void;
  onVerify: () => void;
}

const makeStyles = () => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    alignItems: 'center',
    width: '100%',
  },
  iconContainer: {
    height: '170px',
    width: '274px',
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  contentContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '32px',
    alignItems: 'center',
    width: '430px',
  },
  textContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    alignItems: 'center',
    textAlign: 'center',
    color: '#1E202C',
    width: '430px',
  },
  title: {
    fontFamily: 'Urbanist',
    fontSize: '32px',
    fontWeight: 700,
    lineHeight: '40px',
    color: '#1E202C',
    width: '428px',
  },
  description: {
    fontFamily: 'Inter',
    fontSize: '16px',
    fontWeight: 400,
    lineHeight: '24px',
    color: '#1E202C',
    width: '428px',
  },
  codeInputContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    alignItems: 'center',
    width: '100%',
  },
  codeInputWrapper: {
    display: 'flex',
    gap: '16px',
    width: '390px',
  },
  resendButton: {
    height: '40px',
    width: '256px',
    borderRadius: '8px',
    backgroundColor: 'transparent',
    color: '#2F5DF5',
    fontFamily: 'Inter',
    fontSize: '14px',
    fontWeight: 600,
    lineHeight: '16px',
    textTransform: 'none',
    border: 'none',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: 'transparent',
      textDecoration: 'underline',
    },
  },
  returnedCodeCard: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '16px',
    padding: '12px 16px',
    borderRadius: '8px',
    backgroundColor: '#F5F7FA',
    border: '1px solid #DDE1EE',
    width: '100%',
    maxWidth: '390px',
  },
  returnedCodeText: {
    fontFamily: 'Urbanist',
    fontSize: '20px',
    fontWeight: 700,
    letterSpacing: '4px',
    color: '#1E202C',
  },
  copyButton: {
    textTransform: 'none',
    fontWeight: 600,
    color: '#2F5DF5',
  },
});

export function CodeVerificationStep({
  code,
  onCodeChange,
  maskedPhoneNumber,
  maskedDestination,
  expiresAt,
  returnedCode,
  onResend,
  onVerify,
}: CodeVerificationStepProps) {
  const sx = makeStyles();
  const [copied, setCopied] = useState(false);

  const remainingSeconds = expiresAt ? Math.max(0, Math.floor((expiresAt - Date.now()) / 1000)) : 0;
  const destination = maskedDestination ?? maskedPhoneNumber;

  const handleCopyCode = useCallback(async () => {
    if (!returnedCode) return;
    try {
      await navigator.clipboard.writeText(returnedCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore clipboard errors
    }
  }, [returnedCode]);

  const handleCodeComplete = (completedCode: string) => {
    if (completedCode.length === 6) {
      onVerify();
    }
  };

  return (
    <Box sx={sx.container}>
      <Box sx={sx.contentContainer}>
        <Box sx={sx.textContainer}>
          <Typography sx={sx.title}>
            {maskedDestination
              ? 'Check your email and enter the verification code'
              : 'Check your phone and enter the verification code'}
          </Typography>
          <Typography sx={sx.description}>
            Please enter the <span style={{ fontWeight: 600 }}>6-digit code</span> that we sent to{' '}
            <span style={{ fontWeight: 600 }}>{destination}</span> to finish the verification. The
            code will expire in{' '}
            {remainingSeconds > 0 ? (
              <span style={{ fontWeight: 600 }}>
                <CountdownTimer initialSeconds={remainingSeconds} format="MM:SS" asText />
              </span>
            ) : (
              <span style={{ fontWeight: 600 }}>10 minutes</span>
            )}
            .
          </Typography>
        </Box>
        {returnedCode && (
          <Box sx={sx.returnedCodeCard}>
            <Typography component="span" sx={sx.returnedCodeText}>
              {returnedCode}
            </Typography>
            <Button size="small" onClick={handleCopyCode} sx={sx.copyButton} variant="text">
              {copied ? 'Copied!' : 'Copy'}
            </Button>
          </Box>
        )}
        <Box sx={sx.codeInputContainer}>
          <Box sx={sx.codeInputWrapper}>
            <CodeInput value={code} onChange={onCodeChange} onComplete={handleCodeComplete} />
          </Box>
          <button type="button" onClick={onResend} style={sx.resendButton as React.CSSProperties}>
            Resend Code
          </button>
        </Box>
      </Box>
    </Box>
  );
}
